// Motor de hojas de cálculo: referencias, rangos, fórmulas y calculadora.

export function colName(n) {
  let s = "";
  n = n + 1;
  while (n > 0) {
    const r = (n - 1) % 26;
    s = String.fromCharCode(65 + r) + s;
    n = Math.floor((n - 1) / 26);
  }
  return s;
}

export function colIndex(letters) {
  let n = 0;
  for (const ch of letters.toUpperCase()) n = n * 26 + (ch.charCodeAt(0) - 64);
  return n - 1;
}

function parseRef(ref) {
  const m = /^([A-Za-z]+)(\d+)$/.exec(ref);
  if (!m) return null;
  return { c: colIndex(m[1]), r: parseInt(m[2], 10) - 1 };
}

function rangeRefs(a, b) {
  const pa = parseRef(a), pb = parseRef(b);
  if (!pa || !pb) return [];
  const refs = [];
  const r1 = Math.min(pa.r, pb.r), r2 = Math.max(pa.r, pb.r);
  const c1 = Math.min(pa.c, pb.c), c2 = Math.max(pa.c, pb.c);
  for (let r = r1; r <= r2; r++) for (let c = c1; c <= c2; c++) refs.push({ r, c });
  return refs;
}

const FUNCS = {
  SUMA: (a) => a.reduce((s, x) => s + x, 0),
  SUM: (a) => a.reduce((s, x) => s + x, 0),
  PROMEDIO: (a) => (a.length ? a.reduce((s, x) => s + x, 0) / a.length : 0),
  AVERAGE: (a) => (a.length ? a.reduce((s, x) => s + x, 0) / a.length : 0),
  MAX: (a) => (a.length ? Math.max(...a) : 0),
  MIN: (a) => (a.length ? Math.min(...a) : 0),
  CONTAR: (a) => a.length,
  COUNT: (a) => a.length,
  REDONDEAR: (a) => {
    const p = Math.pow(10, a[1] || 0);
    return Math.round((a[0] || 0) * p) / p;
  },
  ROUND: (a) => {
    const p = Math.pow(10, a[1] || 0);
    return Math.round((a[0] || 0) * p) / p;
  },
};

export function emptyCells(rows = 12, cols = 6) {
  return Array.from({ length: rows }, () => Array.from({ length: cols }, () => ""));
}

function toNum(v) {
  if (v === "" || v == null) return 0;
  if (typeof v === "number") return v;
  const n = parseFloat(String(v).replace(",", "."));
  return isNaN(n) ? 0 : n;
}

// Evalúa una hoja completa. Devuelve { display: string[][] }.
export function computeSheet(data) {
  const nrows = data.length;
  const ncols = nrows ? data[0].length : 0;
  const cache = new Map();
  const inProgress = new Set();
  const raw = (r, c) => (data[r] && data[r][c] != null ? String(data[r][c]) : "");

  function getCell(r, c) {
    if (r < 0 || c < 0 || r >= nrows || c >= ncols) return "";
    const key = r + ":" + c;
    if (cache.has(key)) return cache.get(key);
    if (inProgress.has(key)) { cache.set(key, "#CICLO"); return "#CICLO"; }
    inProgress.add(key);
    const v = raw(r, c).trim();
    let res;
    if (v.startsWith("=")) {
      try { res = evalExpr(v.slice(1), getCell); }
      catch { res = "#ERROR"; }
    } else {
      res = raw(r, c);
    }
    inProgress.delete(key);
    cache.set(key, res);
    return res;
  }

  function evalExpr(src, cellGetter) {
    let i = 0;
    const s = src;
    const skip = () => { while (i < s.length && s[i] === " ") i++; };
    const resolveRef = (ref) => {
      const p = parseRef(ref);
      if (!p) return 0;
      return toNum(cellGetter(p.r, p.c));
    };
    function parseExpr() { return parseAdd(); }
    function parseAdd() {
      let v = parseMul();
      for (;;) { skip(); const c = s[i];
        if (c === "+") { i++; v += parseMul(); }
        else if (c === "-") { i++; v -= parseMul(); }
        else break;
      }
      return v;
    }
    function parseMul() {
      let v = parseUnary();
      for (;;) { skip(); const c = s[i];
        if (c === "*") { i++; v *= parseUnary(); }
        else if (c === "/") { i++; const d = parseUnary(); v = d === 0 ? NaN : v / d; }
        else break;
      }
      return v;
    }
    function parseUnary() {
      skip();
      if (s[i] === "-") { i++; return -parseUnary(); }
      if (s[i] === "+") { i++; return parseUnary(); }
      return parsePrimary();
    }
    function parsePrimary() {
      skip();
      const c = s[i];
      if (c === "(") { i++; const v = parseExpr(); skip(); if (s[i] === ")") i++; return v; }
      if ((c >= "0" && c <= "9") || c === ".") {
        let j = i;
        while (j < s.length && ((s[j] >= "0" && s[j] <= "9") || s[j] === "." || s[j] === ",")) j++;
        const num = parseFloat(s.slice(i, j).replace(",", "."));
        i = j;
        return num;
      }
      if (/[A-Za-zÁÉÍÓÚÑ]/.test(c)) {
        let j = i;
        while (j < s.length && /[A-Za-z0-9ÁÉÍÓÚÑ]/.test(s[j])) j++;
        const name = s.slice(i, j);
        i = j;
        skip();
        if (s[i] === "(") {
          i++;
          const args = parseArgs();
          skip();
          if (s[i] === ")") i++;
          const fn = FUNCS[name.toUpperCase()];
          if (!fn) throw new Error("func");
          return fn(args);
        }
        return resolveRef(name.toUpperCase());
      }
      throw new Error("parse");
    }
    function parseArgs() {
      const nums = [];
      skip();
      if (s[i] === ")") return nums;
      for (;;) {
        skip();
        const m = /^([A-Za-z]+\d+):([A-Za-z]+\d+)/.exec(s.slice(i));
        if (m) {
          i += m[0].length;
          for (const p of rangeRefs(m[1], m[2])) nums.push(toNum(cellGetter(p.r, p.c)));
        } else {
          nums.push(parseExpr());
        }
        skip();
        if (s[i] === ",") { i++; continue; }
        break;
      }
      return nums;
    }
    const result = parseExpr();
    return result;
  }

  const display = [];
  for (let r = 0; r < nrows; r++) {
    const row = [];
    for (let c = 0; c < ncols; c++) {
      const v = getCell(r, c);
      let d;
      if (typeof v === "number") {
        d = isNaN(v) ? "#ERROR" : String(Math.round(v * 1e10) / 1e10);
      } else d = v;
      row.push(d);
    }
    display.push(row);
  }
  return { display };
}

// Calculadora aritmética simple (sin referencias). Lanza si es inválida.
export function calc(src) {
  let i = 0;
  const s = String(src);
  const skip = () => { while (s[i] === " ") i++; };
  function e() { let v = t(); for (;;) { skip(); const c = s[i]; if (c === "+") { i++; v += t(); } else if (c === "-") { i++; v -= t(); } else break; } return v; }
  function t() { let v = u(); for (;;) { skip(); const c = s[i]; if (c === "*") { i++; v *= u(); } else if (c === "/") { i++; const d = u(); v = d === 0 ? NaN : v / d; } else break; } return v; }
  function u() { skip(); if (s[i] === "-") { i++; return -u(); } if (s[i] === "+") { i++; return u(); } return p(); }
  function p() {
    skip();
    const c = s[i];
    if (c === "(") { i++; const v = e(); skip(); if (s[i] === ")") i++; return v; }
    let j = i;
    while (j < s.length && /[0-9.,]/.test(s[j])) j++;
    if (j === i) throw new Error("x");
    const n = parseFloat(s.slice(i, j).replace(",", "."));
    i = j;
    return n;
  }
  const r = e();
  skip();
  if (i < s.length) throw new Error("x");
  if (isNaN(r) || !isFinite(r)) throw new Error("x");
  return Math.round(r * 1e10) / 1e10;
}

// Parsea texto CSV a una matriz 2D (maneja comillas, comas y saltos de línea).
export function parseCSV(text) {
  const s = String(text).replace(/^\ufeff/, "").replace(/\r\n/g, "\n").replace(/\r/g, "\n");
  const rows = [];
  let row = [], field = "", i = 0, inQ = false;
  const firstLine = s.split("\n")[0] || "";
  const sep = firstLine.includes(";") && !firstLine.includes(",") ? ";" : ",";
  while (i < s.length) {
    const c = s[i];
    if (inQ) {
      if (c === '"') {
        if (s[i + 1] === '"') { field += '"'; i += 2; continue; }
        inQ = false; i++; continue;
      }
      field += c; i++; continue;
    }
    if (c === '"') { inQ = true; i++; continue; }
    if (c === sep) { row.push(field); field = ""; i++; continue; }
    if (c === "\n") { row.push(field); rows.push(row); row = []; field = ""; i++; continue; }
    field += c; i++;
  }
  if (field !== "" || row.length) { row.push(field); rows.push(row); }
  const w = rows.reduce((m, r) => Math.max(m, r.length), 0) || 1;
  return rows.map((r) => { while (r.length < w) r.push(""); return r; });
}

// Convierte una hoja a tabla Markdown con valores calculados.
export function sheetToMarkdown(cells) {
  const { display } = computeSheet(cells);
  const cols = display[0]?.length || 0;
  if (!cols) return "";
  const header = Array.from({ length: cols }, (_, c) => colName(c));
  const line = (arr) => `| ${arr.map((x) => String(x || "").replace(/\|/g, "\\|")).join(" | ")} |`;
  const rows = [line(header), line(header.map(() => "---"))];
  for (const row of display) rows.push(line(row));
  return rows.join("\n");
}

// Convierte una hoja a CSV con valores calculados.
export function sheetToCSV(cells) {
  const { display } = computeSheet(cells);
  const esc = (v) => {
    const s = String(v == null ? "" : v);
    return /[",\n;]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  return display.map((row) => row.map(esc).join(",")).join("\r\n");
}

// Genera el HTML de una tabla (valores calculados) para imprimir a PDF.
export function sheetToHTMLTable(cells) {
  const { display } = computeSheet(cells);
  const ncols = display[0]?.length || 0;
  const th = (t) => `<th>${t}</th>`;
  const td = (t) => `<td>${String(t == null ? "" : t).replace(/</g, "&lt;")}</td>`;
  const head = "<tr><th></th>" + Array.from({ length: ncols }, (_, c) => th(colName(c))).join("") + "</tr>";
  const body = display.map((row, r) => `<tr><th class="rownum">${r + 1}</th>` + row.map(td).join("") + "</tr>").join("");
  return `<table><thead>${head}</thead><tbody>${body}</tbody></table>`;
}

// ---- Hojas embebidas dentro de notas (bloque cercado ```notacora-sheet ... ```) ----
export const SHEET_FENCE_RE = /```notacora-sheet\s*\n([\s\S]*?)\n```/g;

export function newSheetId() {
  return "s_" + Math.random().toString(36).slice(2, 9);
}

export function makeSheetFence(id, cells) {
  return "```notacora-sheet\n" + JSON.stringify({ id, cells }) + "\n```";
}

// Actualiza (por id) los datos de una hoja embebida dentro del contenido de la nota.
export function upsertSheetInContent(content, id, cells) {
  const fence = makeSheetFence(id, cells);
  let found = false;
  const out = String(content).replace(SHEET_FENCE_RE, (m, body) => {
    try {
      const o = JSON.parse(body.trim());
      if (o.id === id) { found = true; return fence; }
    } catch { /* ignore */ }
    return m;
  });
  return found ? out : content;
}

// Elimina una hoja embebida (por id) del contenido de la nota.
export function removeSheetFromContent(content, id) {
  return String(content)
    .replace(SHEET_FENCE_RE, (m, body) => {
      try {
        const o = JSON.parse(body.trim());
        if (o.id === id) return "";
      } catch { /* ignore */ }
      return m;
    })
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

// Reemplaza las hojas embebidas por tablas (HTML o Markdown) para exportar a PDF.
export function expandSheetsForExport(content, asHtml = false) {
  return String(content).replace(SHEET_FENCE_RE, (m, body) => {
    try {
      const o = JSON.parse(body.trim());
      return asHtml ? sheetToHTMLTable(o.cells) : sheetToMarkdown(o.cells);
    } catch { return ""; }
  });
}

// Marcador legible que se muestra en el editor de texto en lugar del bloque crudo.
export const SHEET_TOKEN = "📊 [Hoja de cálculo]";

// Convierte el contenido real (con bloques) a lo que se muestra en el textarea.
export function contentToDisplay(content) {
  return String(content).replace(SHEET_FENCE_RE, () => SHEET_TOKEN);
}

// Reconstruye el contenido real a partir del texto mostrado, reinsertando cada bloque por orden.
export function displayToContent(display, content) {
  const fences = [];
  String(content).replace(SHEET_FENCE_RE, (m) => { fences.push(m); return m; });
  let i = 0;
  const re = new RegExp(SHEET_TOKEN.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "g");
  return String(display).replace(re, () => fences[i++] ?? "");
}
